import { PlateRecipe, PlateRect } from '../types';

/**
 * A geometria e a tipografia da placa de posto, sem nada de React.
 *
 * Mora aqui, e não no `StationPlate`, porque a peça é composta DUAS vezes: em
 * DOM, para o palco do diálogo, e em canvas 2D, para os arquivos que descem.
 * Se cada um chutasse o seu próprio arredondamento, o que se vê no preview
 * deixaria de ser o que sai no PNG — que é justamente o contrato que o preview
 * existe para cumprir.
 */

/**
 * A tinta do QR e do código.
 *
 * `#101725` é o mesmo valor cravado no `placa.mjs` ao montar o
 * `placa-exemplo.png` — o render de conferência que prova que a geometria do
 * JSON está certa. Casar com ele é o que faz o preview do admin e a prova do
 * gerador serem a mesma peça.
 *
 * Note que NÃO é `recipe.cores.tintaPainel`: aquela é a tinta do painel da
 * arte, escolhida para contrastar com o campo da marca. O QR e o código moram
 * sobre branco puro, e sobre branco o que vale é o quase-preto.
 */
export const PLATE_INK = '#101725';

const CTTX_SITE = 'cttx.com.br';

/**
 * O que o QR codifica.
 *
 * Mesma URL que o gerador usa na prova. Fica exportado porque quem chamar com
 * código customizado precisa poder montar o mesmo valor — ou passar outro, se
 * um dia o destino do QR deixar de ser o site.
 */
export const plateQrValue = (code: string) => `https://${CTTX_SITE}/${encodeURIComponent(code)}`;

/**
 * Como o código sai impresso na tarja: hífen antes dos três últimos dígitos
 * (`1234567` → `1234-567`).
 *
 * É SÓ a tarja. O QR continua com o código cru: o leitor do app pega o que vem
 * depois da última `/` e só reconhece 7 dígitos — com o hífen dentro, a placa
 * não abriria o abastecimento. Código que não é só dígito sai como veio.
 */
export const plateCodeLabel = (code: string) => code.replace(/^(\d+)(\d{3})$/, '$1-$2');

/** A fonte da tarja do código, idêntica à do render de conferência. */
export const CODE_FONT = '-apple-system, Arial, sans-serif';

/**
 * Qual retângulo do slot consumir.
 *
 * O JSON traz três (`quadrado`, `area`, `caixa`) e um `usar` dizendo qual
 * vale. `caixa` é a caixa do elemento e NÃO deve ser interpretada — ela inclui
 * o que `border-radius` e sombra comem, e desenhar nela encosta o QR na borda.
 * O fallback existe para receita de rodada antiga, que pode não ter o campo.
 */
const rectOf = (slot: { usar?: string } & Record<string, unknown>): PlateRect => {
  const chosen = slot.usar ? slot[slot.usar] : undefined;
  return (chosen || slot.area || slot.caixa) as PlateRect;
};

/**
 * Onde o QR vai, já com o `qrScale` aplicado.
 *
 * Arredondado no espaço do canvas da receita, que é onde a placa é
 * rasterizada em tamanho nativo: meio pixel de tamanho ou de deslocamento
 * borra a aresta dos módulos. Com `qrScale = 1` a conta cai exatamente no
 * canto do slot, com o lado igual à menor dimensão dele.
 */
export const plateQrBox = (recipe: PlateRecipe, qrScale = 1) => {
  const qr = rectOf(recipe.slots.qr);
  const base = Math.min(qr.w, qr.h);
  const size = Math.round(base * qrScale);
  const inset = Math.round((base - size) / 2);
  return { x: qr.x + inset, y: qr.y + inset, size };
};

/** A tarja do código. */
export const plateCodeRect = (recipe: PlateRecipe): PlateRect => rectOf(recipe.slots.codigo);

/**
 * O maior corpo de fonte em que `text` ainda cabe em `rect`.
 *
 * A `fonteSugerida` da receita foi calculada para o código de EXEMPLO
 * (`ABC1-2345`, 9 caracteres) — o próprio JSON avisa: "codigo mais longo
 * precisa encolher para nao encostar nas bordas". Como o código real vem de um
 * endpoint e ninguém garante o comprimento, quem compõe é que tem de resolver,
 * senão a placa de um posto de código longo sai com o texto vazando a tarja.
 *
 * A medição é no canvas 2D, e não medindo o DOM: medir o DOM exigiria um passo
 * de layout e um render a mais, e quem compõe precisa do corpo ANTES de montar
 * a peça. O canvas mede a MESMA fonte, na hora, sem tocar na árvore.
 *
 * O espaçamento encolhe junto, na mesma proporção: reduzir só o corpo e manter
 * o `letter-spacing` original espalha as letras de um código longo e a tarja
 * fica com cara de outra peça.
 */
export const fitFont = (
  text: string,
  rect: PlateRect,
  suggested: { px: number; peso: number; espacamentoPx: number },
): { px: number; spacing: number } => {
  const fallback = { px: suggested.px, spacing: suggested.espacamentoPx };
  if (!text) { return fallback; }

  const context = document.createElement('canvas').getContext('2d');
  if (!context) { return fallback; }

  context.font = `${suggested.peso} ${suggested.px}px ${CODE_FONT}`;
  // `letter-spacing` do CSS entra DEPOIS de cada caractere, o último inclusive
  // — é por isso que a conta usa `length` e não `length - 1`, e é a mesma
  // folga que o `marginRight` negativo devolve na hora de centralizar.
  const needed = context.measureText(text).width + suggested.espacamentoPx * text.length;

  // A margem de 2% cobre a diferença entre a métrica do canvas e a do layout
  // real, que não são bit a bit iguais em toda plataforma. Sem ela, um código
  // no limite passa na medição e encosta na borda no render.
  const room = rect.w * 0.98;
  if (needed <= room) { return fallback; }

  const factor = room / needed;
  return {
    px: Math.max(8, Math.floor(suggested.px * factor)),
    spacing: Math.max(0, Math.floor(suggested.espacamentoPx * factor)),
  };
};
